import React, { useState, useEffect, useCallback } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useIntl } from "react-intl";
import { useDropzone } from "react-dropzone";
import { routes } from "../../constants";
import DOMPurify from 'dompurify';
import api from "../../api";

import SurveyHeader from "./SurveyHeader";
import SurveyTabs from "./SurveyTabs";
import SurveySettingsSection from "./SurveySettingsSection";
import SurveyPreviewSection from "./SurveyPreviewSection";
import SurveyBuilderSection from "./SurveyBuilderSection";
import { isProjectLinkLocked, projectIdOf } from "../Project/projectLink";
import SurveyActions from "./SurveyActions";
import { quillStyles } from "./QuillConfig";
import { likertScaleTypes } from "../../constants/likertScales";
import { effectiveVoice } from "../../constants/voices";
import { DEFAULT_GRADIENT, DEFAULT_SURVEY_APPEARANCE } from "./surveyStyle";

// Helper function to derive scale type by comparing choices with predefined scales
const deriveScaleType = (choices) => {
  if (!choices || choices.length === 0) {
    return "CUSTOM";
  }

  // Extract text values from choices (handle both string arrays and object arrays)
  const choiceTexts = choices.map(choice =>
    typeof choice === 'string' ? choice : choice.text
  ).filter(Boolean);

  if (choiceTexts.length === 0) {
    return "CUSTOM";
  }

  // Compare with each predefined scale type
  for (const scaleType of likertScaleTypes) {
    if (scaleType.id === "CUSTOM") continue;

    const scaleOptions = scaleType.options;

    // Check if the choices match this scale type (order doesn't matter)
    if (choiceTexts.length === scaleOptions.length) {
      // Check if all choices exist in the scale options (case-insensitive)
      const normalizedChoices = choiceTexts.map(text => text.toLowerCase().trim());
      const normalizedOptions = scaleOptions.map(option => option.toLowerCase().trim());

      const allMatch = normalizedChoices.every(choice =>
        normalizedOptions.includes(choice)
      );

      if (allMatch) {
        return scaleType.id;
      }
    }
  }

  // If no exact match found, return CUSTOM
  return "CUSTOM";
};


function SurveyForm({ mode }) {
  const { surveyId } = useParams();
  // "New assessment" on a project's Evidence tab opens /surveys/new?project=<id>,
  // so a new survey starts linked to that project.
  const [searchParams] = useSearchParams();
  const presetProject = mode === "create" ? searchParams.get("project") || "" : "";
  // "New assessment → Modern" opens the builder preset to voice.
  const presetRespondMode = mode === "create" && searchParams.get("mode") === "voice" ? "voice" : "traditional";
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const [loading, setLoading] = useState(mode === "edit");
  const [title, setTitle] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [files, setFiles] = useState([]);
  const [processedFileName, setProcessedFileName] = useState(null);
  // Whether this survey's payload carries an impact-assessment link at all
  // (always for a new survey). An existing one sends the link only when the BE
  // serves the field, so an older BE never sees a field it can't store.
  const [projectLinkSupported, setProjectLinkSupported] = useState(mode === "create");
  const [settings, setSettings] = useState({
    // Basic settings
    title: "",
    description: "",
    isPublic: false,
    // The project it belongs to (Survey.project) and the language it is written
    // in (Survey.language) — see SurveyProjectFields.
    project: presetProject,
    language: "en",
    projectLocked: false,
    // How respondents take it (Survey.respond_mode) and a voice survey's voice.
    respondMode: presetRespondMode,
    voice: "",

    // Appearance settings: the bright blue-and-white default palette (colours,
    // card fill, buttons, confirmation page); backgroundMode is "single",
    // "gradient" or "image"
    ...DEFAULT_SURVEY_APPEARANCE,
    backgroundImage: null,
    backgroundBrightness: 100,
    logo: null,
    logoAlignment: "Left",

    // Behavior settings
    redirectLink: "https://prompta.ai",
    backButtonText: intl.formatMessage({ id: "survey_builder.back", defaultMessage: "Back" }),
    nextButtonText: intl.formatMessage({ id: "survey_builder.next", defaultMessage: "Next" }),
    submitButtonText: intl.formatMessage({ id: "survey_builder.submit", defaultMessage: "Submit" }),
    endMessage: intl.formatMessage({ id: "survey_builder.default_end_message", defaultMessage: "We thank you for your participation, your result has been submitted." }),
    showProgressBar: false,
    showQuestionNumber: false,
  });

  useEffect(() => {
    if (mode !== "edit") return;

    const fetchSurvey = async () => {
      try {
        const survey = await api.getSurvey(surveyId);
        console.log("Fetched survey:", survey);

        setTitle(survey.title);
        setIsPublic(survey.isPubliclyAvailable);
        setProjectLinkSupported(Boolean(survey.hasProjectLink));
        setSettings({
          // Basic settings
          title: survey.title || "",
          description: survey.settings?.description || "",
          isPublic: survey.isPubliclyAvailable || false,
          project: projectIdOf(survey.project),
          language: survey.language || "en",
          respondMode: survey.respondMode || "traditional",
          voice: survey.voice || "",
          projectLocked: isProjectLinkLocked({
            projectLocked: survey.projectLocked,
            completedResponses: survey.completedResponses,
          }),

          // Appearance settings
          surveyColour: survey.settings?.surveyColour || DEFAULT_GRADIENT,
          questionBackground: survey.settings?.questionBackground || "transparent",
          fontColour: survey.settings?.fontColour || "#000000",
          backgroundMode: survey.settings?.backgroundMode || "gradient",
          backgroundColor: survey.settings?.backgroundColor || "#ffffff",
          backgroundImage: survey.settings?.backgroundImage || null,
          backgroundBrightness: survey.settings?.backgroundBrightness || 100,
          logo: survey.settings?.logo || null,
          logoAlignment: survey.settings?.logoAlignment || "Left",
          questionOpacity: survey.settings?.questionOpacity || 0.2,

          // NEW: Question and Choice Font Colors
          questionFontColor: survey.settings?.questionFontColor || "#000000",
          choiceFontColor: survey.settings?.choiceFontColor || "#000000",

          // NEW: Survey Title and Description Font Colors
          surveyTitleColor: survey.settings?.surveyTitleColor || "#000000",
          surveyDescriptionColor: survey.settings?.surveyDescriptionColor || "#000000",

          // Behavior settings
          redirectLink: survey.settings?.redirectLink || "https://prompta.ai",
          backButtonText: survey.settings?.backButtonText || "Back",
          nextButtonText: survey.settings?.nextButtonText || "Next",
          submitButtonText: survey.settings?.submitButtonText || "Submit",
          endMessage: survey.settings?.endMessage || "We thank you for your participation, your result has been submitted.",
          showProgressBar: survey.settings?.showProgressBar || false,
          showQuestionNumber: survey.settings?.showQuestionNumber || false,

          // NEW: Button-specific Colors
          backButtonBackgroundColor: survey.settings?.backButtonBackgroundColor || "#1976d2",
          backButtonFontColor: survey.settings?.backButtonFontColor || "#ffffff",
          nextButtonBackgroundColor: survey.settings?.nextButtonBackgroundColor || "#1976d2",
          nextButtonFontColor: survey.settings?.nextButtonFontColor || "#ffffff",
          submitButtonBackgroundColor: survey.settings?.submitButtonBackgroundColor || "#1976d2",
          submitButtonFontColor: survey.settings?.submitButtonFontColor || "#ffffff",

          // A Modern survey's open-text screen (unset → the runner's defaults)
          recordButtonText: survey.settings?.recordButtonText || "",
          recordButtonBackgroundColor: survey.settings?.recordButtonBackgroundColor || "",
          recordButtonFontColor: survey.settings?.recordButtonFontColor || "",
          voiceOrbColor: survey.settings?.voiceOrbColor || "",

          // NEW: Completion Page Settings
          completionBackgroundGradient: survey.settings?.completionBackgroundGradient || DEFAULT_GRADIENT,
          completionTextColor: survey.settings?.completionTextColor || "#2e7d32",
          completionCardBackgroundColor: survey.settings?.completionCardBackgroundColor || "rgba(255, 255, 255, 0.95)",
          completionCardBackgroundColorAlpha: survey.settings?.completionCardBackgroundColorAlpha || 0.95,
          successTitle: survey.settings?.successTitle || "Thank you!",
          successDescription: survey.settings?.successDescription || "Your response has been recorded successfully.",
        });

        const mappedSteps = survey.questions.map((question, index) => {
          const baseStep = {
            id: question.id,

            type: question.type.toUpperCase(),
            question: DOMPurify.sanitize(question.text) || "<p></p>",
            description: DOMPurify.sanitize(question.description) || "",
            settings: question.settings || {
              styles: {
                question: { fontWeight: "normal", fontStyle: "normal", fontSize: "14px", lineHeight: "1.15", letterSpacing: "0px" },
                description: { fontWeight: "normal", fontStyle: "normal", fontSize: "14px", lineHeight: "1.15", letterSpacing: "0px" },
              },
            },
            tags: question.tags || [],
            isMandatory: question.is_required || false,
          };

          if (question.type === "single_choice" || question.type === "multi_choice") {
            baseStep.choices = question.choices?.map((choice) => ({
              id: choice.id,
              text: choice.text,
              tags: choice.tags || [],
              // The segment choice it stands for, on a segment-linked question.
              segmentChoice: choice.segment_choice ?? null,
            })) || [];
          }
          if (question.type === "single_choice") {
            // The project segment it's linked to (null for none).
            baseStep.segment = question.segment ?? null;
          }

          if (question.type === "likert_scale") {
            // Handle scale options from either 'scale' or 'choices' field
            let scaleOptions = [];
            if (question.scale && question.scale.length > 0) {
              scaleOptions = question.scale.map((option) => ({
                text: option.text || option,
                value: option.value || option.text || option,
                index: option.position || scaleOptions.length,
                tags: option.tags || []
              }));
            } else if (question.choices && question.choices.length > 0) {
              scaleOptions = question.choices.map((choice, idx) => ({
                text: choice.text,
                value: choice.value || choice.text,
                index: choice.position || idx,
                tags: choice.tags || []
              }));
            } else {
              // Default to agreement scale if no options provided
              scaleOptions = [
                { text: "Strongly Disagree", value: "strongly_disagree", index: 0, tags: [] },
                { text: "Disagree", value: "disagree", index: 1, tags: [] },
                { text: "Neutral", value: "neutral", index: 2, tags: [] },
                { text: "Agree", value: "agree", index: 3, tags: [] },
                { text: "Strongly Agree", value: "strongly_agree", index: 4, tags: [] },
              ];
            }

            baseStep.choices = scaleOptions;

            // Derive scale type from the choices
            baseStep.scaleType = deriveScaleType(scaleOptions);

            console.log("Derived scaleType:", baseStep.scaleType, "for choices:", scaleOptions.map(c => c.text));
          }

          return baseStep;
        });

        console.log("mappedSteps:", mappedSteps);
        setSteps(mappedSteps);
      } catch (error) {
        enqueueSnackbar(`Failed to load assessment: ${error.message}`, {
          variant: "error",
          autoHideDuration: 2000,
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
      } finally {
        setLoading(false);
      }
    };
    fetchSurvey();
  }, [mode, surveyId, enqueueSnackbar]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'application/json': ['.json'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setFiles(acceptedFiles);
      }
    },
  });

  useEffect(() => {
    console.log("Updated steps:", steps);
  }, [steps]);

  useEffect(() => {
    if (files.length === 0 && processedFileName) {
      setProcessedFileName(null);
      console.log("File processing cleared");
    }
  }, [files, processedFileName]);

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = quillStyles;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  useEffect(() => {
    document.body.style.backgroundColor = "#f5f5f5";
    return () => {
      document.body.style.backgroundColor = "";
    };
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate survey title is required
    if (!settings.title || settings.title.trim() === "") {
      enqueueSnackbar(intl.formatMessage({ id: "survey.title_required" }) || "Assessment title is required", {
        variant: "error",
        autoHideDuration: 2000,
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
      return;
    }

    const invalidQuestions = steps.filter(
      (step) => step.type !== "PAGE_BREAK" && step.type !== "TEXT" && (!step.question || step.question === "<p><br></p>")
    );
    if (invalidQuestions.length > 0) {
      enqueueSnackbar(intl.formatMessage({ id: "survey.save_validation_failed" }), {
        variant: "error",
        autoHideDuration: 2000,
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
      return;
    }

    const transformedQuestions = steps.map((step, index) => {
      console.log(step)
      const questionData = {
        id: step.id,
        text: DOMPurify.sanitize(step.question || ""),
        description: DOMPurify.sanitize(step.description || ""),
        type: step.type.toLowerCase(),
        is_required: step.isMandatory || false,
        position: index,
        settings: {
          styles: step.settings?.styles || {
            question: { fontWeight: "normal", fontStyle: "normal", fontSize: "14px", lineHeight: "1.15", letterSpacing: "0px" },
            description: { fontWeight: "normal", fontStyle: "normal", fontSize: "14px", lineHeight: "1.15", letterSpacing: "0px" },
          },
        },
        tags: step.tags || [],
      };

      if (step.type === "SINGLE_CHOICE" || step.type === "MULTI_CHOICE") {
        // Ids ride along so the BE updates the same option rows (and their
        // answers keep pointing at them) instead of recreating every option.
        questionData.choices = step.choices?.map((choice, choiceIndex) => ({
          id: choice.id,
          text: DOMPurify.sanitize(choice.text || ""),
          position: choiceIndex,
          tags: choice.tags
        })) || [];
      }
      if (step.type === "SINGLE_CHOICE") {
        // A linked question's choices are the segment's; the BE rewrites them.
        questionData.segment = step.segment ?? null;
      }

      if (step.type === "LIKERT_SCALE") {
        // Save scaleType to preserve it when loading the survey
        questionData.scaleType = step.scaleType || "AGREEMENT";

        if (step.scaleType === "CUSTOM") {
          questionData.scale = (step.choices || []).map((option, optionIndex) => ({
            text: DOMPurify.sanitize(option?.text || ""),
            position: optionIndex,
          }));
          // Also save customChoiceCount for CUSTOM scales
          if (step.customChoiceCount) {
            questionData.customChoiceCount = step.customChoiceCount;
          }
        } else {
          // Use the new choices structure for Likert scales
          const scaleOptions = step.choices && step.choices.length > 0
            ? step.choices.map(choice => choice.text)
            : ["Strongly Disagree", "Disagree", "Neutral", "Agree", "Strongly Agree"];

          questionData.scale = scaleOptions.map((option, optionIndex) => ({
            text: DOMPurify.sanitize(option || ""),
            position: optionIndex,
          }));
        }
      }

      return questionData;
    });

    const payload = {
      title: DOMPurify.sanitize(title),
      language: settings.language || "en",
      respond_mode: settings.respondMode || "traditional",
      voice: effectiveVoice(settings.voice),
      // The impact-assessment link (null = none), while it can still change
      // (the BE owns the lock).
      ...(projectLinkSupported && !settings.projectLocked
        ? { project: settings.project ? Number(settings.project) : null }
        : {}),
      // is_publicly_available: isPublic,
      settings: {
        surveyColour: settings.surveyColour,
        questionBackground: settings.questionBackground,
        fontColour: settings.fontColour,
        backgroundMode: settings.backgroundMode,
        backgroundColor: settings.backgroundColor,
        backgroundImage: settings.backgroundImage,
        backgroundBrightness: settings.backgroundBrightness,
        questionOpacity: settings.questionOpacity,
        logo: settings.logo,
        logoAlignment: settings.logoAlignment,
        redirectLink: DOMPurify.sanitize(settings.redirectLink),
        backButtonText: DOMPurify.sanitize(settings.backButtonText),
        nextButtonText: DOMPurify.sanitize(settings.nextButtonText),
        submitButtonText: DOMPurify.sanitize(settings.submitButtonText),
        endMessage: DOMPurify.sanitize(settings.endMessage),
        showProgressBar: settings.showProgressBar,
        showQuestionNumber: settings.showQuestionNumber,

        // NEW: Question and Choice Font Colors
        questionFontColor: settings.questionFontColor,
        choiceFontColor: settings.choiceFontColor,

        // NEW: Survey Title and Description Font Colors
        surveyTitleColor: settings.surveyTitleColor,
        surveyDescriptionColor: settings.surveyDescriptionColor,

        // NEW: Button-specific Colors
        backButtonBackgroundColor: settings.backButtonBackgroundColor,
        backButtonFontColor: settings.backButtonFontColor,
        nextButtonBackgroundColor: settings.nextButtonBackgroundColor,
        nextButtonFontColor: settings.nextButtonFontColor,
        submitButtonBackgroundColor: settings.submitButtonBackgroundColor,
        submitButtonFontColor: settings.submitButtonFontColor,

        // A Modern survey's open-text screen: its Record button and the voice orb
        recordButtonText: DOMPurify.sanitize(settings.recordButtonText || ""),
        recordButtonBackgroundColor: settings.recordButtonBackgroundColor,
        recordButtonFontColor: settings.recordButtonFontColor,
        voiceOrbColor: settings.voiceOrbColor,

        // NEW: Completion Page Settings
        completionBackgroundGradient: settings.completionBackgroundGradient,
        completionTextColor: settings.completionTextColor,
        completionCardBackgroundColor: settings.completionCardBackgroundColor,
        completionCardBackgroundColorAlpha: settings.completionCardBackgroundColorAlpha,
        successTitle: DOMPurify.sanitize(settings.successTitle),
        successDescription: DOMPurify.sanitize(settings.successDescription),

        // NEW: Survey Description
        description: DOMPurify.sanitize(settings.description),
      },
      questions: transformedQuestions,
    };

    try {
      let response;
      if (mode === "create") {
        console.log("Creating survey with payload:", JSON.stringify(payload, null, 2));
        response = await api.createSurvey(payload);
        enqueueSnackbar(intl.formatMessage({ id: "survey.create_success" }), {
          variant: "success",
          autoHideDuration: 2000,
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
        navigate(routes.surveys);
      } else {
        console.log("Updating survey with payload:", JSON.stringify(payload, null, 2));
        console.log("SurveyColour in payload:", payload.settings.surveyColour);
        console.log("Current settings.surveyColour:", settings.surveyColour);
        response = await api.updateSurvey(surveyId, payload);
        enqueueSnackbar(intl.formatMessage({ id: "survey.update_success" }), {
          variant: "success",
          autoHideDuration: 2000,
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
        navigate(`${routes.surveys}/${surveyId}`);
      }
    } catch (error) {
      let errorMessage = mode === "create" ? intl.formatMessage({ id: "survey.create_failed" }) : intl.formatMessage({ id: "survey.update_failed" });
      if (error.response?.data) {
        const { data } = error.response;
        if (data.error) {
          errorMessage = data.error;
        } else if (typeof data === 'object') {
          const validationErrors = Object.values(data).flat().join("; ");
          errorMessage = validationErrors || errorMessage;
        }
      } else {
        errorMessage = error.message || errorMessage;
      }

      enqueueSnackbar(errorMessage, {
        variant: "error",
        autoHideDuration: 2000,
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
      console.error(`Failed to ${mode} survey:`, errorMessage);
    }
  };

  const handleChoiceChange = useCallback((questionIndex, choiceIndex, field, value) => {
    console.log(`Updating choice for question at index ${questionIndex}, choice ${choiceIndex}:`, { field, value });
    if (isDeleting) {
      console.log("Choice change ignored: Deletion in progress");
      return;
    }
    setSteps(prev => {
      const updatedSteps = [...prev];
      if (field === "choices" && choiceIndex === null) {
        // Handle array update for choices (e.g., removal)
        updatedSteps[questionIndex] = {
          ...updatedSteps[questionIndex],
          choices: value,
        };
      } else if (updatedSteps[questionIndex] && updatedSteps[questionIndex].choices[choiceIndex]) {
        // Handle individual choice updates (e.g., text, filterTags)
        updatedSteps[questionIndex].choices[choiceIndex][field] = value;
      }
      return updatedSteps;
    });
  }, [isDeleting]);

  const handleQuestionChange = useCallback((index, field, value) => {
    console.log(`Updating question at index ${index}:`, { field, value });
    if (isDeleting) {
      console.log("Question change ignored: Deletion in progress");
      return;
    }
    setSteps(prev => {
      const updatedSteps = [...prev];
      if (updatedSteps[index]) {
        updatedSteps[index][field] = value;
      }
      return updatedSteps;
    });
  }, [isDeleting]);

  const handleQuestionSettingsChange = useCallback((index, settingsField, value) => {
    console.log(`Updating settings for question at index ${index}:`, { settingsField, value });
    if (isDeleting) {
      console.log("Settings change ignored: Deletion in progress");
      return;
    }
    setSteps(prev => {
      const updatedSteps = [...prev];
      if (updatedSteps[index]) {
        updatedSteps[index].settings = {
          ...updatedSteps[index].settings,
          [settingsField]: value,
        };
      }
      return updatedSteps;
    });
  }, [isDeleting]);

  // Handle global survey settings changes
  const handleGlobalSettingsChange = useCallback((field, value) => {
    console.log(`Updating global setting:`, { field, value });
    setSettings(prev => ({
      ...prev,
      [field]: value,
    }));
    // Segments belong to a project: moving the survey to another one (or to
    // none) unlinks every question. Their choices stay, as ordinary ones.
    if (field === "project") {
      setSteps(prev => prev.map(step => (step.segment ? { ...step, segment: null } : step)));
    }

    // Also update title and isPublic in their respective states for compatibility
    if (field === 'title') {
      setTitle(value);
    } else if (field === 'isPublic') {
      setIsPublic(value);
    }
  }, []);

  const handleQuestionStylesChange = useCallback((index, styleType, styleKey, value) => {
    console.log(`Updating styles for question at index ${index}:`, { styleType, styleKey, value });
    if (isDeleting) {
      console.log("Styles change ignored: Deletion in progress");
      return;
    }
    setSteps(prev => {
      const updatedSteps = [...prev];
      if (updatedSteps[index]) {
        updatedSteps[index].settings.styles = {
          ...updatedSteps[index].settings.styles,
          [styleType]: {
            ...updatedSteps[index].settings.styles[styleType],
            [styleKey]: value,
          },
        };
      }
      return updatedSteps;
    });
  }, [isDeleting]);

  const handleAddChoice = useCallback((questionIndex) => {
    console.log(`Adding choice to question at index ${questionIndex}`);
    if (isDeleting) {
      console.log("Add choice ignored: Deletion in progress");
      return;
    }
    setSteps(prev => {
      const updatedSteps = [...prev];
      if (updatedSteps[questionIndex]) {
        updatedSteps[questionIndex].choices.push({
          text: `Choice ${updatedSteps[questionIndex].choices.length + 1}`,
          value: `choice_${updatedSteps[questionIndex].choices.length + 1}`,
          index: updatedSteps[questionIndex].choices.length,
          tags: [],
        });
      }
      return updatedSteps;
    });
  }, [isDeleting]);

  const handleDeleteQuestion = useCallback((index) => {
    console.log("Deleting question at index:", index);
    setIsDeleting(true);
    setSteps(prev => {
      const updatedSteps = prev.filter((_, i) => i !== index);
      console.log("updatedSteps:", updatedSteps);
      if (updatedSteps.length === 0) {
        setProcessedFileName(null);
        console.log("All questions deleted, resetting processedFileName");
      }
      return updatedSteps;
    });

    setCurrentStep(prev => {
      if (steps.length === 1) {
        return 0;
      }
      return index <= prev ? Math.max(0, prev - 1) : prev;
    });

    setIsDeleting(false);
    console.log("Deletion flag reset");
  }, [steps.length]);

  const handleDuplicateQuestion = useCallback((index) => {
    if (isDeleting) {
      console.log("Duplicate ignored: Deletion in progress");
      return;
    }
    console.log(`Duplicating question at index ${index}`);
    setSteps(prev => {
      const updatedSteps = [...prev];
      const questionToDuplicate = { ...updatedSteps[index] };
      questionToDuplicate.id = `${questionToDuplicate.type}-${Date.now()}`;
      if (questionToDuplicate.type === "SINGLE_CHOICE" || questionToDuplicate.type === "MULTI_CHOICE") {
        // New option rows for the copy; and a segment takes one question, so
        // the copy starts unlinked (its choices stay, as ordinary ones).
        questionToDuplicate.choices = questionToDuplicate.choices.map(({ id, segmentChoice, ...choice }) => ({
          ...choice,
          tags: [...choice.tags],
        }));
        if (questionToDuplicate.segment) questionToDuplicate.segment = null;
      }
      if (questionToDuplicate.type === "LIKERT_SCALE") {
        questionToDuplicate.choices = [...(questionToDuplicate.choices || [])];
        questionToDuplicate.scaleType = questionToDuplicate.scaleType || "AGREEMENT";
      }
      updatedSteps.splice(index + 1, 0, questionToDuplicate);
      return updatedSteps;
    });
    setCurrentStep(index + 1);
  }, [isDeleting]);

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress />
        <Box sx={{ mt: 2 }}>Loading survey...</Box>
      </Box>
    );
  }
  console.log("isPublic :----", isPublic)

  return (
    <Box
      sx={{
        maxWidth: "1500px",
        margin: "0 auto",
        padding: { xs: "16px", sm: "24px", md: "32px" },
        minHeight: "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
        pt: { xs: 2, sm: 4, md: 6 },
      }}
    >

      {tabValue === 0 && (
        <>
          <SurveyBuilderSection
            steps={steps}
            setSteps={setSteps}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            handleQuestionChange={handleQuestionChange}
            handleQuestionSettingsChange={handleQuestionSettingsChange}
            handleGlobalSettingsChange={handleGlobalSettingsChange}
            handleQuestionStylesChange={handleQuestionStylesChange}
            handleAddChoice={handleAddChoice}
            handleChoiceChange={handleChoiceChange}
            handleDeleteQuestion={handleDeleteQuestion}
            handleDuplicateQuestion={handleDuplicateQuestion}
            isDeleting={isDeleting}
            setIsDeleting={setIsDeleting}
            settings={settings}
            files={files}
            setFiles={setFiles}
            processedFileName={processedFileName}
            setProcessedFileName={setProcessedFileName}
            enqueueSnackbar={enqueueSnackbar}
            getRootProps={getRootProps}
            getInputProps={getInputProps}
          />
        </>
      )}

      {tabValue === 1 && (
        <SurveySettingsSection
          settings={settings}
          setSettings={setSettings}
        />
      )}

      {tabValue === 2 && (
        <SurveyPreviewSection
          steps={steps}
          settings={settings}
          title={title}
        />
      )}

      <SurveyActions
        mode={mode}
        onSubmit={handleSubmit}
        onCancel={() => navigate(routes.surveys)}
      />
    </Box>
  );
}

export default SurveyForm;
